import React, { useEffect, useState } from "react";
import { CompanyInfo, IQuotationData } from "../../models/quotationData.model";
import { format } from "date-fns";
import { IOrderConfirmationData } from "../../models/epcCustomer.model";
import OptimizedConfirmOrderModal from "./OptimizedConfirmOrderModal";
import ILeadData, { IStateDistrict } from "../../models/LeadData.model";
import { IProduct } from "../../models/productData.model";
import { HandPrintAndDownload } from "../../components/Assets/generateBill/HandlePrintAndDownload";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import SendToWhatsApp from "../../components/SendToWhatsapp/SendToWhatsApp";
import Button, { ButtonType } from "../../components/Assets/Button";
import QuotationLogModal from "./QuotationLogModal";
import { fetchQuotationLogs } from "../../store/slices/quotationSlice";
import { CustomerOrder, OrderItem } from "../../models/Order.model";
import { handleOrderConfirmationPDF } from "../EpcQuotation/PrintAndDownloadOrder";
import toast from "react-hot-toast";
import { calculateOrderTotals } from "../../utils/orderTotals";
import { getProductById } from "../../store/slices/productSlice";
import { ChevronDown, Edit, Package2, X } from "lucide-react";

interface QuotationCardProps {
  quotation: IQuotationData;
  onConfirm?: (
    quotationId: string,
    orderData: IOrderConfirmationData,
  ) => Promise<string>;
  lead: ILeadData | null;
  statesData: IStateDistrict[];
  onEdit?: (q: IQuotationData) => void;
}

const OptimizedQuotationCard: React.FC<QuotationCardProps> = ({
  quotation,
  onConfirm,
  lead,
  statesData,
  onEdit,
}) => {
  const {
    quotationNumber,
    createdAt,
    items,
    transportPrice,
    discountPrice,
    confirmedQuotation,
    _id,
  } = quotation;

  const dispatch = useDispatch<RootDispatch>();

  const QuotationCreatedBy = useSelector(
    (state: RootState) => state.auth.userName?.username,
  );
  const QuotationCreatedByNumber = useSelector(
    (state: RootState) => state.auth.mobileNumber,
  );

  const [showLogsModal, setShowLogsModal] = useState(false);
  const quotationLogs = useSelector(
    (state: RootState) => state.quotations.quotationLogs,
  );
  const loading = useSelector((state: RootState) => state.quotations.loading);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const visibleItems = items.slice(0, 2);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [mobileNumberInput, setMobileNumberInput] = useState(
    lead?.mobileNumber || "",
  );

  const orderCreatedBy = useSelector(
    (state: RootState) => state.auth.userName?.username,
  );
  const orderCreatedByNumber = useSelector(
    (state: RootState) => state.auth.mobileNumber,
  );
  const [orderData, setOrderData] = useState<IOrderConfirmationData>({
    name: "",
    email: "",
    mobileNumber: "",
    transportPrice: transportPrice || 0,
    discountPrice: discountPrice || 0,
    billingAddress: {
      line1: "",
      city: "",
      state: "",
      pincode: "",
    },
    shippingAddress: {
      line1: "",
      city: "",
      state: "",
      pincode: "",
    },
    gstin: "",
    sameAsBilling: true,
  });

  useEffect(() => {
    if (lead) {
      setOrderData((order) => ({
        ...order,
        name: lead.salesRepId?.companyName || lead.name,
        email: lead.email || "",
        mobileNumber: lead.mobileNumber,
        gstin: lead.gstin || "",
        billingAddress: {
          line1: lead.address?.line1 || "",
          city: lead.address?.city || "",
          state: lead.address?.state || "",
          pincode: lead.address?.pincode || "",
        },
        shippingAddress: {
          line1: lead.address?.line1 || "",
          city: lead.address?.city || "",
          state: lead.address?.state || "",
          pincode: lead.address?.pincode || "",
        },
      }));
    }
  }, [lead]);

  useEffect(() => {
    setOrderData((prev) => ({
      ...prev,
      transportPrice: quotation.transportPrice || 0,
      discountPrice: quotation.discountPrice || 0,
    }));
  }, [quotation.transportPrice, quotation.discountPrice]);

  const getNormalizedItem = (item: any) => {
    const isMaterial = item.type === "material";
    const detail = isMaterial ? item.rawMaterial : item.product;

    const name =
      (typeof detail === "object"
        ? detail?.productName || detail?.name
        : null) ||
      item.productName ||
      item.name;

    const id = (typeof detail === "object" ? detail?._id : detail) || item._id;

    if (!name && !id) return null;

    return {
      _id: id,
      name: name || "Unknown Item",
      gst: item.gst || (typeof detail === "object" ? detail?.gst : 0) || 0,
      hsn: item.hsn || (typeof detail === "object" ? detail?.hsn : 0) || 0,
      msp: !isMaterial ? (typeof detail === "object" ? detail?.msp : 0) : 0,
      range: !isMaterial
        ? typeof detail === "object"
          ? detail?.range
          : undefined
        : undefined,
      unit:
        item.unit ||
        (typeof detail === "object" ? detail?.unit || detail?.unitType : ""),
    };
  };

  const handleConfirmOrder = async () => {
    if (!onConfirm) return;
    setIsSubmitting(true);

    try {
      const res = await onConfirm(_id, orderData);

      const productsToFetch = quotation.items.filter(
        (item) => item.type !== "material" && item.product,
      );

      const productIds = productsToFetch.map((item) => {
        const p = item.product;
        return typeof p === "object" ? p._id : p;
      });

      let fetchedProducts: IProduct[] = [];
      if (productIds.length > 0) {
        fetchedProducts = await dispatch(getProductById(productIds)).unwrap();
      }

      const productMap = new Map<string, IProduct>(
        fetchedProducts.map((p) => [p._id, p]),
      );

      const selectedGoods = quotation.items.map((item) => {
        const isMaterial = item.type === "material";
        const detail = isMaterial ? item.rawMaterial : item.product;
        const targetId = typeof detail === "object" ? detail?._id : detail;

        if (isMaterial) {
          return {
            _id: targetId,
            label:
              (typeof detail === "object" ? detail.name : null) ||
              item.productName ||
              item.name,
            quantity: item.quantity,
            price: item.price!,
            gst: item.gst || 0,
            hsn: item.hsn || 0,
          };
        }

        const freshProduct = productMap.get(targetId);

        return {
          _id: targetId,
          label:
            freshProduct?.productName ||
            (typeof detail === "object" ? detail?.productName : null) ||
            item.productName ||
            item.name,
          quantity: item.quantity,
          price: item.price!,
          gst: freshProduct?.gst || item.gst || 0,
          hsn: freshProduct?.hsn || item.hsn || 0,
        };
      });

      const orderItems: OrderItem[] = selectedGoods.map((item) => {
        const original = quotation.items.find((q) => {
          const detail = q.type === "material" ? q.rawMaterial : q.product;
          const id = typeof detail === "object" ? detail?._id : detail;
          return id === item._id;
        });

        return {
          productName: item.label,
          price: item.price,
          quantity: item.quantity,
          transportPrice: transportPrice,
          discountPrice: original?.discount || 0,
        };
      });

      const customerOrders: CustomerOrder[] = selectedGoods.map((item) => {
        const original = quotation.items.find((q) => {
          const detail = q.type === "material" ? q.rawMaterial : q.product;
          const id = typeof detail === "object" ? detail?._id : detail;
          return id === item._id;
        });

        return {
          _id: item._id,
          productName: item.label,
          hsn: item.hsn,
          price: item.price,
          gst: item.gst,
          productId:
            original?.type === "material"
              ? original.rawMaterial
              : original?.product,
          quantity: item.quantity,
          transportPrice: transportPrice,
          discountPrice: original?.discount || 0,
        };
      });

      const grandTotalForPdf = calculateOrderTotals(
        orderItems,
        customerOrders,
      ).grandTotal;

      const companyInfo: CompanyInfo = {
        name: orderData?.name,
        email: orderData?.email || "",
        contactPersonName: lead?.contactPersonName || "",
        mobileNumber: orderData?.mobileNumber || "",
        billingAddress: orderData.billingAddress.line1,
        billingCity: orderData.billingAddress.city,
        billingState: orderData.billingAddress.state,
        billingPincode: orderData.billingAddress.pincode,
        orderDate: new Date().toLocaleDateString(),
        orderNumber: res!,
        orderCreatedBy: orderCreatedBy,
        orderCreatedByNumber: orderCreatedByNumber,
        shippingAddress: orderData.shippingAddress.line1,
        shippingCity: orderData.shippingAddress.city,
        shippingState: orderData.shippingAddress.state,
        shippingPincode: orderData.shippingAddress.pincode,
        gstin: orderData.gstin,
      };

      await handleOrderConfirmationPDF(
        orderItems,
        companyInfo,
        customerOrders,
        grandTotalForPdf,
        transportPrice || 0,
        discountPrice || 0,
      );

      setIsConfirmModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to confirm order");
    } finally {
      setIsSubmitting(false);
    }
  };

  const pdfProducts: IProduct[] = quotation.items.map((it) => {
    const norm = getNormalizedItem(it);
    return {
      _id: norm?._id || it._id,
      productName: norm?.name || it.productName || "Unknown Item",
      quantity: it.quantity,
      price: it.price ?? 0,
      gst: it.gst ?? 0,
      hsn: it.hsn ?? 0,
      msp: norm?.msp ?? 0,
      range: typeof norm?.range === "string" ? undefined : norm?.range,
      unit: norm?.unit || it.unit,
      discount: it.discount || 0,
      type: it.type,
    } as IProduct;
  });

  const companyInfo: CompanyInfo = {
    name: lead?.salesRepId?.companyName || lead?.name || "",
    email: lead?.email || "",
    contactPersonName: lead?.contactPersonName || "",
    mobileNumber: lead?.mobileNumber || "",
    billingAddress: lead?.address?.line1 || "",
    billingCity: lead?.address?.city || "",
    billingState: lead?.address?.state || "",
    billingPincode: lead?.address?.pincode || "",
    quotationNumber: quotation.quotationNumber || "",
    quotationDate: quotation.createdAt,
    quotationCreatedBy: QuotationCreatedBy,
    QuotationCreatedByNumber: QuotationCreatedByNumber,
  };

  const handleViewLogs = () => {
    setShowLogsModal(true);
    dispatch(fetchQuotationLogs(quotation._id));
  };
  const orderItems = quotation.items.map((item) => ({
    productName: getNormalizedItem(item)?.name || item.productName || "Item",
    price: item.price ?? 0,
    quantity: item.quantity,
    transportPrice: transportPrice || 0,
    discountPrice: item.discount || 0,
  }));

  const customerOrders = quotation.items.map((item) => {
    const normalized = getNormalizedItem(item);

    return {
      _id: normalized?._id || item._id,
      productName: normalized?.name || item.productName || "Item",
      hsn: item.hsn || 0,
      price: item.price ?? 0,
      gst: item.gst || 0,
      productId: normalized?._id || item._id,
      quantity: item.quantity,
      transportPrice: transportPrice || 0,
      discountPrice: item.discount || 0,
    };
  });

  const totals = calculateOrderTotals(orderItems, customerOrders);
  return (
    <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-3 flex flex-col h-[500px] hover:shadow-lg transition-all duration-200">
      <div className="flex-1">
        <div className="flex justify-between items-start mb-2 gap-2">
          <h3 className="text-[11px] font-bold text-slate-700 leading-tight">
            Quotation: #{quotationNumber || "—"}
          </h3>
          {!confirmedQuotation && (
            <Edit
              size={16}
              onClick={() => onEdit?.(quotation)}
              className="cursor-pointer hover:scale-110 duration-200 text-slate-700"
            />
          )}
        </div>

        <div className="flex justify-between items-center mb-2">
          <h3 className="text-[9px] font-medium text-slate-500">
            Created By: {QuotationCreatedBy}
          </h3>
          <h3 className="text-[9px] font-medium text-slate-500">
            {format(new Date(createdAt), "dd/MM/yyyy")}
          </h3>
        </div>

        <div className="space-y-1.5 h-[80px] overflow-hidden">
          {visibleItems.map((item, i) => {
            const displayData = getNormalizedItem(item);
            if (!displayData) return null;

            return (
              <div
                key={i}
                className="bg-slate-50 border border-slate-100 px-2.5 py-1.5 rounded-lg text-[10px] h-[36px] flex items-center"
              >
                <div className="flex w-full items-center gap-1.5 text-slate-700">
                  <span className="flex-1 font-semibold truncate uppercase tracking-wide">
                    {displayData.name}
                  </span>

                  <span className="px-1.5 text-center font-semibold text-sky-700 bg-sky-100 py-0.5 rounded-md whitespace-nowrap shrink-0 text-[9px]">
                    x{item.quantity} {displayData.unit || ""}
                  </span>

                  <span className="w-[72px] text-right font-bold text-slate-900 shrink-0">
                    ₹{((item.price ?? 0) * item.quantity).toLocaleString()}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Empty placeholders for equal height */}
          {Array.from({
            length: Math.max(0, 2 - visibleItems.length),
          }).map((_, idx) => (
            <div
              key={`empty-${idx}`}
              className="h-[36px] rounded-lg border border-dashed border-slate-100 bg-slate-50/40"
            />
          ))}
        </div>

        {items.length > 2 && (
          <button
            onClick={() => setShowProductsModal(true)}
            className="mt-3 w-full py-1.5 flex items-center justify-center gap-1 text-[10px] font-bold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors uppercase tracking-widest border border-dashed border-blue-200"
          >
            <>
              View All {items.length - 2} Products <ChevronDown size={14} />
            </>
          </button>
        )}
      </div>
      <div className="mt-3 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[10px] font-bold tracking-widest uppercase text-slate-500">
            Summary
          </h4>

          <span
            className={`text-[9px] px-2 py-1 rounded-full font-bold ${
              confirmedQuotation
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {confirmedQuotation ? "Confirmed" : "Pending"}
          </span>
        </div>

        <div className="space-y-1.5 text-[11px]">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal</span>
            <span className="font-semibold">
              ₹{Number(totals.subTotal || 0).toLocaleString()}
            </span>
          </div>

          <div className="flex justify-between text-slate-600">
            <span>Tax</span>
            <span className="font-semibold text-blue-600">
              ₹{Number(totals.totalTax || 0).toLocaleString()}
            </span>
          </div>

          {transportPrice > 0 && (
            <div className="flex justify-between text-slate-600">
              <span>Transport</span>
              <span className="font-semibold text-orange-600">
                ₹{Number(transportPrice || 0).toLocaleString()}
              </span>
            </div>
          )}

          {discountPrice > 0 && (
            <div className="flex justify-between text-slate-600">
              <span>Discount</span>
              <span className="font-semibold text-red-500">
                - ₹{Number(discountPrice || 0).toLocaleString()}
              </span>
            </div>
          )}

          <div className="border-t border-dashed border-slate-300 pt-2 mt-2 flex justify-between items-center">
            <span className="text-[12px] font-bold text-slate-800 uppercase tracking-wide">
              Total
            </span>

            <span className="text-[15px] font-extrabold text-emerald-600">
              ₹{Number(totals.grandTotal || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-auto flex flex-col gap-1.5 pt-3 border-t border-slate-100">
        {!confirmedQuotation && (
          <Button
            type={ButtonType.Submit}
            onClick={() => setIsConfirmModalOpen(true)}
            className="w-full !px-2 !py-1.5 !text-[10px] rounded-lg font-semibold "
          >
            Confirm Quotation
          </Button>
        )}
        <div className="flex gap-1">
          <Button
            type={ButtonType.Extra}
            onClick={() =>
              HandPrintAndDownload(
                pdfProducts,
                companyInfo,
                transportPrice,
                discountPrice,
              )
            }
            className="w-1/2 !px-2 !py-1.5 !text-[10px] rounded-lg font-semibold "
          >
            Open Quotation
          </Button>
          <Button
            type={ButtonType.Extra}
            onClick={() => setShowWhatsAppModal(true)}
            className="w-1/2 !px-2 !py-1.5 !text-[10px] rounded-lg font-semibold "
          >
            Send to WhatsApp
          </Button>
        </div>
        <Button
          type={ButtonType.Cancel}
          onClick={handleViewLogs}
          className="w-full !px-2 !py-1.5 !text-[10px] !text-slate-600 rounded-lg !bg-slate-100 border border-slate-200 font-semibold"
        >
          View Logs
        </Button>
      </div>
      {showProductsModal && (
        <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <div>
                <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Package2 size={16} />
                  All Products
                </h2>

                <p className="text-[11px] text-slate-500 mt-0.5">
                  Quotation #{quotationNumber}
                </p>
              </div>

              <button
                onClick={() => setShowProductsModal(false)}
                className="h-8 w-8 rounded-lg hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[65vh] overflow-y-auto p-4 space-y-2">
              {items.map((item, i) => {
                const displayData = getNormalizedItem(item);

                if (!displayData) return null;

                return (
                  <div
                    key={i}
                    className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-[11px] font-bold text-slate-800 uppercase truncate">
                          {displayData.name}
                        </h4>

                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[10px] px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 font-semibold">
                            Qty: {item.quantity}
                          </span>

                          {displayData.unit && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-200 text-slate-700 font-medium">
                              {displayData.unit}
                            </span>
                          )}

                          {item.gst && item.gst > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 font-medium">
                              GST {item.gst}%
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-slate-500">Amount</p>

                        <p className="text-sm font-extrabold text-slate-900">
                          ₹
                          {((item.price ?? 0) * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <div className="bg-white border border-slate-200 rounded-lg p-2">
                  <p className="text-slate-500">Subtotal</p>
                  <p className="font-bold text-slate-800">
                    ₹{Number(totals.subTotal || 0).toLocaleString()}
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-2">
                  <p className="text-slate-500">Tax</p>
                  <p className="font-bold text-blue-600">
                    ₹{Number(totals.totalTax || 0).toLocaleString()}
                  </p>
                </div>

                {transportPrice > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-2">
                    <p className="text-slate-500">Transport</p>
                    <p className="font-bold text-orange-600">
                      ₹{Number(transportPrice || 0).toLocaleString()}
                    </p>
                  </div>
                )}

                {discountPrice > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-2">
                    <p className="text-slate-500">Discount</p>
                    <p className="font-bold text-red-500">
                      - ₹{Number(discountPrice || 0).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex justify-between items-center">
                <span className="text-sm font-bold text-emerald-800">
                  Grand Total
                </span>

                <span className="text-xl font-extrabold text-emerald-700">
                  ₹{Number(totals.grandTotal || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <QuotationLogModal
        isOpen={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        quotationLogs={quotationLogs}
        loading={loading}
      />

      <OptimizedConfirmOrderModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleConfirmOrder}
        orderData={orderData}
        setOrderData={setOrderData}
        isSubmitting={isSubmitting}
        statesData={statesData}
        items={items}
      />

      {showWhatsAppModal && (
        <SendToWhatsApp
          transportPrice={transportPrice}
          discountPrice={discountPrice}
          selectedCompanyInfo={companyInfo}
          setSelectedCompanyInfo={() => {}}
          selectedProducts={pdfProducts}
          setSelectedProducts={() => {}}
          showMobileNumberModal={showWhatsAppModal}
          setShowMobileNumberModal={setShowWhatsAppModal}
          mobileNumberInput={mobileNumberInput}
          setMobileNumberInput={setMobileNumberInput}
          currentLead={lead}
          type="quotation"
        />
      )}
    </div>
  );
};

export default OptimizedQuotationCard;
